import React, { useState, useEffect } from 'react';
import './EfficiencyFiles.css';
import TeamHeader from '../TeamHeader/TeamHeader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { IoOptionsOutline } from "react-icons/io5";
import { FaBoltLightning } from "react-icons/fa6";
import { LiaGlobeAmericasSolid } from "react-icons/lia";
import { MdInsertDriveFile } from "react-icons/md";

// Interfaces
interface FileEntry {
    name: string;
    url: string;
    type: 'Global' | 'Reference' | 'Lightning';
    region: string;
    date: string;
}

const EfficiencyFiles = () => {

    // Variables
    const [files, setFiles] = useState<FileEntry[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());

    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<FileEntry["type"][]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        const fetchFiles = async () => {
            try {
                const [effResp, refResp] = await Promise.all([
                    fetch('/apidet/detector/efficiency/dataUSA/', { credentials: "same-origin" }),
                    fetch('/apidet/detector/referencedata/dataUSA/', { credentials: "same-origin" }),
                ]);

                if (!effResp.ok || !refResp.ok) {
                    throw new Error('Failed to fetch files');
                }

                const effData = await effResp.json();
                const refData = await refResp.json();

                // Map to FileEntry[]
                const mapFile = (file: { name: string; url: string }): FileEntry => {
                    // Determine type
                    let type: FileEntry['type'] = 'Global';
                    if (/table_lightning/i.test(file.name)) type = 'Lightning';
                    else if (/GlobalEFF/i.test(file.name)) type = 'Global';
                    else type = 'Reference';

                    // Region extraction
                    const regionMatch = file.url.match(/\/download\/([^/]+)\//);
                    const region = regionMatch ? regionMatch[1] : 'Unknown';

                    // Date extraction: match first YYYY-MM-DD in the filename
                    const dateMatch = file.name.match(/(\d{4}-\d{2}-\d{2})/);
                    const date = dateMatch ? dateMatch[1] : '';

                    return { ...file, type, region, date };
                };


                const allFiles: FileEntry[] = [
                    ...effData.dataUSA.map((f: any) => mapFile(f)),
                    ...refData.dataUSA.map((f: any) => mapFile(f)),
                ];

                // Sort by date descending
                allFiles.sort((a, b) => {
                    return new Date(b.date).getTime() - new Date(a.date).getTime();
                });

                setFiles(allFiles);
            } catch (err) {
                console.error(err);
                toast.error('Error fetching files');
            }
        };

        fetchFiles();
    }, []);

    // Handle checkbox toggle
    const toggleFileSelection = (url: string) => {
        setSelectedFiles((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(url)) newSet.delete(url);
            else newSet.add(url);
            return newSet;
        });
    };

    // Download a single file
    const downloadFile = async (file: FileEntry): Promise<boolean> => {
        try {
            const apiUrl =
                file.type === 'Reference'
                    ? `/apidet/detector/referencedata/download/dataUSA/${file.name}`
                    : `/apidet/detector/efficiency/download/dataUSA/${file.name}`;

            const response = await fetch(apiUrl, {
                credentials: "same-origin",
                headers: {
                    'X-Filepath': file.url,
                },
            });

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(objectUrl);
            return true;
        } catch {
            return false;
        }
    };




    // Export selected files as CSV
    const exportSelected = () => {
        if (selectedFiles.size === 0) {
            toast.warning('No files selected');
            return;
        }

        const selected = files.filter((f) => selectedFiles.has(f.url));
        const headers = ['File Name', 'Type', 'Region', 'Date', 'URL'];
        const rows = selected.map((f) => [f.name, f.type, f.region, f.date, f.url]);
        const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `efficiency_files_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success(`${selected.length} files exported successfully to document!`);
    };

    // Setting filter options
    const regions = Array.from(new Set(files.map(f => f.region)));
    const types: FileEntry["type"][] = ["Global", "Reference", "Lightning"];

    const filteredFiles = files
        .filter(file => {
            const fileTime = new Date(file.date).getTime();
            const startTime = startDate ? new Date(startDate).getTime() : null;
            const endTime = endDate ? new Date(endDate).getTime() : null;

            // Filter by region, type, date
            const passesFilter =
                (selectedRegions.length === 0 || selectedRegions.includes(file.region)) &&
                (selectedTypes.length === 0 || selectedTypes.includes(file.type)) &&
                (!startTime || fileTime >= startTime) &&
                (!endTime || fileTime <= endTime);

            // Filter by search query
            const passesSearch =
                searchQuery.trim() === '' ||
                file.name.toLowerCase().includes(searchQuery.trim().toLowerCase());

            return passesFilter && passesSearch;
        });



    return (
        <div className="customer-directory-body">
            <ToastContainer />

            <TeamHeader title="TOA" />

            <div className="customer-title-and-expand-buttons">
                <h1 className="cutomer-title">Efficiency and Reference Files</h1>
            </div>

            <div className="hardware3-action-buttons">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', paddingRight: '1rem' }}>
                    <input
                        type="text"
                        placeholder="Search by file name..."
                        className="hardware3-open-popup-button"
                        style={{ marginRight: '0.5rem' }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />



                    {/* TOA Efficiency Filter */}
                    <div className="filter-wrapper" style={{ position: 'relative' }}>
                        <button onClick={() => setFilterDropdownOpen(prev => !prev)}>
                            <IoOptionsOutline className='customer-filter-icon' /> Filter
                        </button>

                        {filterDropdownOpen && (
                            <div className="filter-dropdown toa-efficiency-dropdown">
                                <div style={{ display: 'flex', gap: '1rem' }}>

                                    {/* Region */}
                                    <div className="filter-section">
                                        <h4>Region</h4>
                                        {regions.map(region => (
                                            <label key={region} className="filter-option">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRegions.includes(region)}
                                                    onChange={(e) => {
                                                        setSelectedRegions(prev =>
                                                            e.target.checked
                                                                ? [...prev, region]
                                                                : prev.filter(r => r !== region)
                                                        );
                                                    }}
                                                />
                                                {region}
                                            </label>
                                        ))}
                                    </div>

                                    {/* Type */}
                                    <div className="filter-section">
                                        <h4>Type</h4>
                                        {types.map(type => (
                                            <label key={type} className="filter-option">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTypes.includes(type)}
                                                    onChange={(e) => {
                                                        setSelectedTypes(prev =>
                                                            e.target.checked
                                                                ? [...prev, type]
                                                                : prev.filter(t => t !== type)
                                                        );
                                                    }}
                                                />
                                                {type}
                                            </label>
                                        ))}
                                    </div>

                                    {/* Date */}
                                    <div className="filter-section">
                                        <h4>Date</h4>

                                        <label className="filter-option filter-option-for-date">
                                            <p>From</p>
                                            <input
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                        </label>

                                        <label className="filter-option filter-option-for-date">
                                            <p>To</p>
                                            <input
                                                type="date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </label>
                                    </div>



                                </div>

                                <div className="filter-actions">
                                    <button
                                        onClick={() => {
                                            setSelectedRegions([]);
                                            setSelectedTypes([]);
                                            setStartDate('');
                                            setEndDate('');

                                        }}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>



                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div className="actions-dropdown-wrapper" style={{ position: 'relative' }}>
                        <select
                            className="hardware3-open-popup-button hardware3-filter-select"
                            onChange={(e) => {
                                const action = e.target.value;
                                e.target.value = '';
                                const bulkFiles = files.filter(f => selectedFiles.has(f.url) && filteredFiles.includes(f));

                                if (action === 'download') {
                                    const bulkDownload = async () => {
                                        if (bulkFiles.length === 0) {
                                            toast.warning('No files selected');
                                            return;
                                        }

                                        const toastId = toast.info(`Downloading ${bulkFiles.length} files...`, { autoClose: false });

                                        let success = 0;
                                        let failed = 0;

                                        for (const file of bulkFiles) {
                                            const ok = await downloadFile(file);
                                            ok ? success++ : failed++;
                                        }

                                        toast.dismiss(toastId);

                                        if (failed === 0) {
                                            toast.success(`${success} files downloaded successfully`);
                                        } else {
                                            toast.error(`${success} files downloaded, ${failed} failed`);
                                        }
                                    };
                                    bulkDownload();
                                }

                                if (action === 'export') {
                                    exportSelected(); // You can pass filteredFiles to exportSelected if needed
                                }
                            }}
                        >
                            <option value="" disabled selected>
                                Actions
                            </option>
                            <option value="download">Download</option>
                            <option value="export">Export</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="hardware3-table-wrapper">
                <table className="hardware3-table toa-efficiency-table">
                    <thead>
                        <tr>
                            <th colSpan={2}>
                                <div className="toa-efficiency-cell">
                                    <input
                                        type="checkbox"
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedFiles(new Set(filteredFiles.map(f => f.url)));
                                            } else {
                                                setSelectedFiles(new Set());
                                            }
                                        }}
                                        checked={
                                            filteredFiles.length > 0 &&
                                            filteredFiles.every(f => selectedFiles.has(f.url))
                                        }
                                    />


                                    <div>File Name</div>
                                </div>
                            </th>
                            <th>Type</th>
                            <th>Region</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredFiles.map(file => (
                            <tr key={file.url}>
                                <td colSpan={2}>
                                    <div className="toa-efficiency-cell">
                                        <input
                                            type="checkbox"
                                            onChange={() => toggleFileSelection(file.url)}
                                            checked={selectedFiles.has(file.url)}
                                        />
                                        <div>{file.name}</div>
                                    </div>
                                </td>
                                <td>
                                    <div className="toa-efficiency-cell">
                                        {file.type === 'Lightning' && <FaBoltLightning />}
                                        {file.type === 'Global' && <LiaGlobeAmericasSolid />}
                                        {file.type === 'Reference' && <MdInsertDriveFile />}
                                        <div>{file.type}</div>
                                    </div>

                                </td>
                                <td>{file.region}</td>
                                <td>{file.date}</td>
                            </tr>
                        ))}
                    </tbody>

                </table>
            </div>
        </div>
    );
};

export default EfficiencyFiles;
